import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';
import linkstate from 'linkstate';

import ImageScroller from '../../components/imagescroller';
import Placeholder from '../../components/gui/placeholder';
import MarkdownDisplay from '../../components/gui/markdownDisplay';
import LazyImage from '../../components/gui/lazyImage';
import Likes from '../../components/likes';

import localUtil from '../../lib/util';
import EmojiPicker from '../gui/emojiPicker';
import ImageScrollerFullscreen from '../gui/imageScrollerFullscreen';
import Avatar from '../gui/avatar';
import MentionTypeahead from '../mentions/typeahead';
import { detectMentionQuery, insertMention, linkifyMentions, extractValidMentions, MAX_MENTIONS } from '../../lib/mentions';
import { openUserProfileDrawer } from '../../lib/userProfile';
import { quoteSnippet } from '../../lib/inboxReply';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';


const REACTION_TYPES = [
    { type: 'like', icon: 'fa-thumbs-up', color: '#1877f2' },
    { type: 'love', icon: 'fa-heart', color: '#e74c3c' },
    { type: 'haha', icon: 'fa-face-laugh-squint', color: '#f1c40f' },
    { type: 'wow', icon: 'fa-face-surprise', color: '#f1c40f' },
    { type: 'sad', icon: 'fa-face-sad-tear', color: '#f1c40f' },
    { type: 'angry', icon: 'fa-face-angry', color: '#e67e22' },
    { type: 'stars', icon: 'fa-face-grin-stars', color: '#f1c40f' },
    { type: 'think', icon: 'fa-face-thinking', color: '#f1c40f' },
    { type: 'fire', icon: 'fa-fire-flame-curved', color: '#e67e22' },
];

@observer
class Comments extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            body: '',
            comments: null,
            showAllComments: false,
            mentions: [],
            mentionQuery: null,
        };
        this.inputRef = null;
    }

    loadAll = async (props = this.props) => {
        const { comments = [] } = props;
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const userHascommented = comments.find(comment => comment.user === user.id);
        if (userHascommented) {
            this.setState({ hascommented: true });
        }
    }

    toggleShowAllComments = () => {
        this.setState(prev => ({ showAllComments: !prev.showAllComments }));
    }

    onInput = e => {
        e.stopPropagation();
        e.preventDefault();
        const mentionQuery = detectMentionQuery(e.target.value, e.target.selectionStart);
        this.setState({ body: e.target.value, mentionQuery });
    }

    onKeyDown = e => {
        e.stopPropagation();
        if (e.isComposing || e.key === 'Process') return;

        const { mentionQuery } = this.state;
        if (mentionQuery && this.mentionTypeaheadRef && this.mentionTypeaheadRef.handleKey(e.key)) {
            e.preventDefault();
            return;
        }

        if (e.key === 'Enter') {
            if (e.shiftKey) {
                return; // allow newline
            }
            e.preventDefault();
            this.addComment();
        }
    }

    onFocus = e => {
        this.setState({
            showAddCommentEdit: true,
        })
    }

    onBlur = e => {
        // this.setState({
        //     showAddCommentEdit: false,
        // });
    }

    onMentionSelect = (user) => {
        const input = this.inputRef;
        const { body, mentionQuery, mentions } = this.state;
        if (!input || !mentionQuery) return;
        const { newText, newCaret } = insertMention(body, mentionQuery.startIndex, input.selectionStart, user.name);
        this.setState({
            body: newText,
            mentionQuery: null,
            mentions: [...mentions.filter(m => m.id !== user.id), user],
        }, () => {
            input.focus();
            input.setSelectionRange(newCaret, newCaret);
            localUtil.resizeTextarea(input);
        });
    }

    closeMentions = () => {
        this.setState({ mentionQuery: null });
    }

    cancelReply = () => {
        this.setState({ replyToCommentId: null });
    }

    addComment = async () => {
        const { body, replyToCommentId, sending = false } = this.state;
        const trimmedBody = (body || '').trim();
        if (!trimmedBody || sending) {
            return;
        }
        const { commentStore } = this.props.stores;
        const { comment = {} } = commentStore;
        const { images = [] } = comment;
        const { type, objectId } = this.props;
        const validMentions = extractValidMentions(trimmedBody, this.state.mentions).slice(0, MAX_MENTIONS);

        this.setState({ sending: true });
        try {
            if (replyToCommentId) {
                await commentStore.insert({
                    type: 'comment',
                    objectId: replyToCommentId,
                    body: trimmedBody,
                    images,
                    ...(validMentions.length > 0 ? { mentions: validMentions } : {}),
                });
                this.setState({ hascommented: true, body: '', showAddCommentEdit: false, replyToCommentId: null, mentions: [], mentionQuery: null }, () => {
                    localUtil.resizeTextarea(this.inputRef);
                });
            } else {
                await commentStore.insert({
                    type,
                    objectId,
                    body: trimmedBody,
                    images,
                    ...(validMentions.length > 0 ? { mentions: validMentions } : {}),
                });
                this.setState({ hascommented: true, body: '', showAddCommentEdit: false, mentions: [], mentionQuery: null }, () => {
                    localUtil.resizeTextarea(this.inputRef);
                });
            }

            const comments = await commentStore.load({
                query: { type, objectId },
                skipUpdate: true,
            });
            this.setState({ comments });

            commentStore.updateKeyValue('comment', {});
            this.toggleComment();
        } finally {
            this.setState({ sending: false });
        }
    }

    removeComment = async (e) => {
        e.stopPropagation();
        e.preventDefault();
        const dialogAction = beginDialogAction(this, 'removeComment');
        if (!dialogAction) return false;
        try {


            const { commentid, objectid } = e.target.closest('button').dataset;
            console.log('removeComment', commentid, e.target, e.target.closest('button'));
            if (!commentid) {
                return;
            }
            if (!((await appConfirm('Are you sure you want to delete this comment?')) && dialogAction.isCurrent())) {
                return;
            }
            const { type, objectId } = this.props;
            const { commentStore } = this.props.stores;
            await commentStore.delete(commentid, 'id', { objectId: objectid });

            const comments = await commentStore.load({
                query: { type, objectId },
                skipUpdate: true,
            });
            this.setState({ comments });
        } finally {
            dialogAction.finish();
        }
    }

    editComment = async () => {
        const { body } = this.state;
        const { type, objectId } = this.props;
        const { commentStore } = this.props.stores;
        const updateObject = {
            id: 1,
            type,
            objectId,
            body: this.state.body,
        };
        await commentStore.update(updateObject);

        const comments = await commentStore.load({
            query: { type, objectId },
            skipUpdate: true,
        });
        this.setState({ comments });
    }

    toggleComment = async () => {
        const { commentStore } = this.props.stores;
        const { showAddComment = {} } = commentStore;
        const { type, objectId } = this.props;

        const addComment = { ...showAddComment };
        addComment[`${type}-${objectId}`] = !addComment[`${type}-${objectId}`];
        commentStore.updateKeyValue('showAddComment', addComment);
    }

    toggleEmojiPicker = (e) => {
        e.stopPropagation();
        e.preventDefault();
        const { showEmojiPicker } = this.state;
        // Toggle the visibility of the emoji picker
        this.setState({
            showEmojiPicker: !showEmojiPicker,
        });
    }

    openImagePicker = (e) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.multiple = true;
        input.style.position = 'fixed';
        input.style.left = '-9999px';
        input.style.top = '0';
        input.style.opacity = '0';
        input.addEventListener('change', async (ev) => {
            await this.handleImageFilesSelected({ target: ev.target });
            try { document.body.removeChild(input); } catch (err) { /* ignore */ }
        });
        document.body.appendChild(input);
        input.click();
    }

    uploadImageFile = (file) => {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('files[]', file, encodeURIComponent(file.name));
            const xhr = new XMLHttpRequest();
            xhr.addEventListener('readystatechange', () => {
                if (xhr.readyState === 4) {
                    if (xhr.status === 201 || xhr.status === 200) {
                        try {
                            const response = JSON.parse(xhr.responseText);
                            const uploadedFile = response?.data?.files?.[0];
                            if (uploadedFile) {
                                resolve(uploadedFile);
                            } else {
                                reject(new Error('No file returned'));
                            }
                        } catch (err) {
                            reject(err);
                        }
                    } else {
                        reject(new Error(`Upload failed: ${xhr.status}`));
                    }
                }
            });
            xhr.open('POST', `${util.getApiServer()}/api/fileupload`);
            xhr.setRequestHeader('Authorization', `Bearer ${util.getJwtToken()}`);
            xhr.send(formData);
        });
    }

    handleImageFilesSelected = async (e) => {
        const input = e.target;
        if (!input.files || input.files.length === 0) {
            return;
        }
        const { commentStore } = this.props.stores;
        const files = Array.from(input.files);
        this.setState({ uploadingImages: true });
        try {
            for (let i = 0; i < files.length; i += 1) {
                const uploaded = await this.uploadImageFile(files[i]);
                const withKey = {
                    ...uploaded,
                    md5: uploaded.md5 || `${uploaded.s3Link || uploaded.name || ''}-${Date.now()}-${i}`,
                };
                const current = commentStore.comment?.images || [];
                commentStore.updateObjectKeyValue('comment', 'images', [...current, withKey]);
            }
        } catch (err) {
            console.error('Comment image upload failed', err);
        } finally {
            this.setState({ uploadingImages: false });
            input.value = '';
        }
    }

    removeAttachedImage = (e) => {
        e.stopPropagation();
        e.preventDefault();
        const { md5 } = e.currentTarget.dataset;
        if (!md5) return;
        const { commentStore } = this.props.stores;
        const current = commentStore.comment?.images || [];
        commentStore.updateObjectKeyValue('comment', 'images', current.filter(img => img.md5 !== md5));
    }

    addEmoji = (emoji) => {
        // Get field by inputRef
        const input = this.inputRef;
        if (input) {
            // Insert emoji at the cursor position
            const start = input.selectionStart;
            const end = input.selectionEnd;
            const value = input.value || '';
            const newValue = value.slice(0, start) + emoji + value.slice(end);
            input.value = newValue;
            // Trigger input event to update the field
            input.dispatchEvent(new Event('input', { bubbles: true }));
        }
    }

    toggleCommentReply = (e) => {
        e.stopPropagation();
        e.preventDefault();
        const { commentid } = e.target.dataset;
        if (!commentid) {
            return;
        }
        const { appState } = this.props.stores;
        const { appContainer } = appState;

        // console.log('toggleCommentReply', commentid, e.target, e.target.closest('small'));
        this.setState({
            replyToCommentId: parseInt(commentid, 10) || null,
        }, () => {
            if (this.inputRef) {
                // Scroll inputref into view
                // const appContainer = document.querySelector('.appContainer');
                // if (!appContainer) {
                //     return;
                // }
                appContainer.scrollTop = this.inputRef.offsetTop; // Adjust the offset as needed
                this.inputRef.focus();

                // Scroll to the input field
                // const rect = this.inputRef.getBoundingClientRect();
                // appContainer.scrollTo({
                //     top: rect.top + 300, // Adjust the offset as needed
                //     left: rect.left,
                //     behavior: 'smooth',
                // });
            }
        });
    }

    onImageClick = (e) => {
        // onClick={onImageClick}
        // data-objectId={id}
        // data-md5={img.md5}
        // data-image={showImg(img)}
        // data-largeimage={showLargeImage(img)}
        e.stopPropagation();
        e.preventDefault();
        const { objectid, md5, image, largeimage } = e.target.dataset;
        // console.log('onImageClick', { objectid, md5, image, largeimage });
        if (!image) {
            return;
        }
        const { showImagescrollerFullscreen } = this.state;
        this.setState({
            showImagescrollerFullscreen: !showImagescrollerFullscreen,
        });
    }

    onClickCloseImageScrollerFullscreen = () => {
        this.setState({
            showImagescrollerFullscreen: false,
        });
    }

    /*
addImages = async obj => {
        const { holdingStore, object, objectName, callback = () => {}, isAdmin, field = 'images' } = this.props;
        const localValue = object[field] ? [...object[field]] : [];
        // console.log({ isAdmin, localValue, id: object.id, name: field, value: obj.value })
        if (object.id) {
            if (isAdmin) {
                await holdingStore.adminUpdate(object.id, { [field]: obj.value }, objectName);
            } else {
                // console.log('doAdminUpdate !isAdmin', { id: idAsInt, name, value });
                await holdingStore.saveField(object.id, field, obj.value);
            }
            holdingStore.updateField(object.id, field, [...localValue, ...obj.value]);
        }
        if (objectName) {
            holdingStore.updateObjectKeyValue(objectName, field, [...localValue, ...obj.value]);
        }
        callback();
	}
    */

    onInputImage = async (obj) => {
        console.log('onInputImage', obj);
    }

    componentDidMount() {
        this.loadAll();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.comments !== this.props.comments) {
            // Fresh comments from a feed refresh replace the copy cached by
            // the last add/edit/delete, so new comments and replies show up.
            this.setState({ comments: null });
            this.loadAll(nextProps);
        }
    }

    getReactionIcons = (likes) => {
        if (!likes || likes.length === 0) return null;

        const reactionCounts = likes.reduce((acc, like) => {
            const type = like.likeType || 'like';
            acc[type] = (acc[type] || 0) + 1;
            return acc;
        }, {});

        const topReactions = Object.entries(reactionCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3);

        return topReactions.map(([type]) => {
            const reaction = REACTION_TYPES.find(r => r.type === type);
            if (!reaction) return null;
            return (
                <i
                    key={type}
                    class={`fa-solid ${reaction.icon}`}
                    style={`color: ${reaction.color}; font-size: 0.65rem;`}
                />
            );
        });
    }

    openCreatorProfile = (comment) => (event) => {
        event.stopPropagation();
        const { appState } = this.props.stores;
        openUserProfileDrawer(appState, {
            userId: comment.user,
            name: comment.creator,
            image: comment.creatorImage,
            faceBox: comment.creatorImageFaceBox,
        });
    }

    renderComment = ({ comment, isOwnComment, isSubComment, parentComment }) => {
        const {
            localLikes = [],
        } = this.state;
        const {
            objectId,
        } = this.props;

        const { userStore, presenceStore } = this.props.stores;
        const { user = {}, language, isBlockedFromComments } = userStore;
        const canReply = user.id && !isBlockedFromComments;
        const avatarSize = isSubComment ? 28 : 32;
        if (comment.user > 0) {
            presenceStore.track(comment.user);
        }

        return (
            <div class='d-flex gap-2 mb-2 w-100' key={comment.id}>
                <span
                    style={comment.user > 0 ? 'cursor: pointer;' : ''}
                    onClick={comment.user > 0 ? this.openCreatorProfile(comment) : undefined}
                >
                    <Avatar name={comment.creator} image={comment.creatorImage} faceBox={comment.creatorImageFaceBox} size={avatarSize} online={presenceStore.onlineByUserId[comment.user]} />
                </span>
                <div class='d-flex flex-column flex-grow-1' style='min-width: 0;'>
                    <div class='bg-body-secondary rounded-3 px-3 py-2 position-relative align-self-start' style='max-width: 100%;'>
                        <div class='fw-semibold small'>
                            <span
                                style={comment.user > 0 ? 'cursor: pointer;' : ''}
                                onClick={comment.user > 0 ? this.openCreatorProfile(comment) : undefined}
                            >
                                {comment.creator}
                            </span>
                        </div>
                        <div class='commentBody'>
                            <MarkdownDisplay markdown={localUtil.replaceImages(linkifyMentions(comment.body, comment.mentions), comment.images, language, false)} markedOpts={localUtil.getMarkdownOptions()} />
                        </div>
                        {comment.likes && comment.likes.length > 0 ? (
                            <div
                                class='likesCount position-absolute d-flex align-items-center rounded-pill px-1 py-0 border'
                                style={`
                                    bottom: -8px; right: -10px;
                                    background-color: var(--bs-body-bg);
                                    font-size: 0.65rem;
                                    line-height: 1rem;
                                    gap: 2px;
                                `}
                            >
                                {this.getReactionIcons(comment.likes)}
                                <span class='ms-1 text-body-secondary'>{comment.likes.length}</span>
                            </div>
                        ) : null}
                    </div>

                    {comment.images && comment.images.length > 0 ? (
                        <div class='d-flex mt-2'>
                            <ImageScroller
                                images={comment.images}
                                stores={this.props.stores}
                                showImg={img => localUtil.displayImg(img, this.props, '800x')}
                                showLargeImage={img => localUtil.displayImg(img, this.props, '1920x')}
                                maxHeight={'30vh'}
                                maxWidth={'270px'}
                                classNamesWrapper1={`mt-1`}
                                classNames={'mt-1'}
                                wrapperMaxWidth={'270px'}
                                onImageClick={this.onImageClick}
                            />
                        </div>
                    ) : null}

                    <div class='d-flex flex-row align-items-center mt-1 gap-3 ps-2'>
                        <small class='text-body-secondary likes-comment'>{localUtil.getTimeAgo(comment.createdDate)}</small>
                        <Likes
                            stores={this.props.stores}
                            type={'comment'}
                            objectId={comment.id}
                            uuidv4={comment.uuidv4}
                            likes={comment.likes}
                            inputLikes={localLikes}
                            showSimpleLike={true}
                            className='likes-comment'
                            shareTitle={comment.body}
                            shareSummary={comment.body}
                            shareImage={comment.images?.[0]?.s3SmallLink}
                        />
                        {canReply && (
                            <small
                                class='text-body-secondary likes-comment'
                                style='cursor: pointer;'
                                onClick={this.toggleCommentReply}
                                data-commentid={isSubComment && parentComment?.id ? parentComment.id : comment.id}
                            >
                                <Text id='comment.reply'>Reply</Text>
                            </small>
                        )}
                        {isOwnComment && (
                            <button
                                class='btn btn-link btn-sm text-body-secondary p-0 likes-comment'
                                onClick={this.removeComment}
                                data-commentid={comment.id}
                                data-objectid={isSubComment ? parentComment.id : objectId}
                            >
                                <i class='fa-regular fa-trash'></i>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const {
            showEmojiPicker = false,
            showAddCommentEdit,
            comments: localComments,
            body,
            localLikes = [],
            replyToCommentId,
            showImagescrollerFullscreen = false,
            showAllComments = false,
            uploadingImages = false,
            sending = false,
        } = this.state;
        const {
            comments = [],
            type,
            objectId,
        } = this.props;
        const { appState, userStore, commentStore } = this.props.stores;
        const { user, isBlockedFromComments } = userStore;
        const isLoggedIn = user.id;
        const canComment = isLoggedIn && !isBlockedFromComments;

        const { showAddComment, comment } = commentStore;

        const finalComments = localComments || comments;
        const commentsToShow = showAllComments ? finalComments : finalComments.slice(-3);
        const hasMoreComments = finalComments.length > 3;

        const replyToComment = finalComments.find(comment => comment.id === replyToCommentId);

        return (<>
            <div class='w-100 d-flex flex-column justify-content-between position-relative text-muted px-3 py-2'>
                {/* {JSON.stringify({ comment })} */}
                {/* <xmp>{JSON.stringify(finalComments, null, 2)}</xmp> */}

                {hasMoreComments && !showAllComments && (
                    <button class='btn btn-link btn-sm text-muted w-100 py-2' onClick={this.toggleShowAllComments}>
                        <i class='fa-regular fa-chevron-up me-2' />
                        <Text id='comment.showEarlierComments' fields={{ count: finalComments.length - 3 }}>Show {finalComments.length - 3} earlier comments</Text>
                    </button>
                )}

                {commentsToShow.map(comment => {
                    const isOwnComment = comment.user === user.id;

                    return (<>
                        {this.renderComment({ comment, isOwnComment })}
                        {comment.comments && comment.comments.length > 0 ? (
                            <div style='padding-left: 40px;'>
                                {comment.comments.map(subComment => {
                                    const isOwnSubComment = subComment.user === user.id;
                                    return this.renderComment({ comment: subComment, isOwnComment: isOwnSubComment, isSubComment: true, parentComment: comment });
                                })}
                            </div>
                        ) : null}
                        {showImagescrollerFullscreen ? (
                            <ImageScrollerFullscreen
                                stores={this.props.stores}
                                images={comment.images}
                                onClickClose={this.onClickCloseImageScrollerFullscreen}
                            />
                        ) : null}
                    </>);
                })}

                {hasMoreComments && showAllComments && (
                    <button class='btn btn-link btn-sm text-muted w-100 py-2' onClick={this.toggleShowAllComments}>
                        <i class='fa-regular fa-chevron-down me-2' />
                        <Text id='comment.showFewerComments'>Vis færre kommentarer</Text>
                    </button>
                )}

                {/* TODO: Consider when to show this */}
                {canComment ? <>
                    {true || showAddComment[`${type}-${objectId}`] || (isLoggedIn && finalComments.length === 0) ? <>
                        {replyToComment && <div class='d-flex align-items-center gap-2 w-100 mb-2 border-top pt-2'>
                            <Avatar name={replyToComment.creator} image={replyToComment.creatorImage} faceBox={replyToComment.creatorImageFaceBox} size={24} />
                            <small class='text-body-secondary flex-grow-1 text-truncate'>
                                <Text id='comment.replyingTo' fields={{ name: replyToComment.creator }}>Replying to {'{{name}}'}</Text>: {quoteSnippet(replyToComment.body, 60)}
                            </small>
                            <button type='button' class='btn btn-sm btn-link text-body-secondary p-0' onClick={this.cancelReply}>
                                <i class='fa-solid fa-xmark' />
                            </button>
                        </div>}

                        <div class='input-group'>
                            <Localizer>
                                <textarea
                                    ref={c => this.inputRef = c}
                                    class='form-control '
                                    placeholder={<Text id='comment.addComment'>Add a comment...</Text>}
                                    onInput={this.onInput}
                                    rows={1}
                                    onKeyDown={this.onKeyDown}
                                    onKeyUp={localUtil.resizeTextarea}
                                    onFocus={this.onFocus}
                                    onBlur={this.onBlur}
                                    value={body}
                                ></textarea>
                            </Localizer>
                            {this.state.mentionQuery && (
                                <MentionTypeahead
                                    ref={c => this.mentionTypeaheadRef = c}
                                    stores={this.props.stores}
                                    query={this.state.mentionQuery.query}
                                    onSelect={this.onMentionSelect}
                                    onClose={this.closeMentions}
                                />
                            )}
                            <button
                                type='button'
                                class='btn btn-primary'
                                onClick={this.addComment}
                                disabled={sending || !(body || '').trim()}
                            >
                                <i class={`fa-duotone ${sending ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} />
                            </button>
                        </div>
                        {showAddCommentEdit ? <>
                            <div
                                class='d-flex flex-row align-items-center'
                            >
                                <button type='button' class='btn btn-lg btn-link text-muted p-0' onClick={this.toggleEmojiPicker}>
                                    <i class={`${showEmojiPicker ? 'fa-solid' : 'fa-regular'} fa-face-smile`} />
                                </button>
                                <button
                                    type='button'
                                    class='btn btn-lg btn-link text-muted p-0 ms-2'
                                    onClick={this.openImagePicker}
                                >
                                    <i class='fa-regular fa-image' />
                                </button>
                                {uploadingImages && (
                                    <span class='ms-2 text-body-secondary small'>
                                        <i class='fa-duotone fa-spinner fa-spin me-1' />
                                        <Text id='comment.uploading-image'>Uploading...</Text>
                                    </span>
                                )}
                            </div>
                            {showEmojiPicker && <>
                                <EmojiPicker stores={this.props.stores} addEmoji={this.addEmoji} />
                            </>}
                            {comment?.images?.length > 0 && (
                                <div class='d-flex flex-row flex-wrap gap-2 mt-2'>
                                    {comment.images.map(img => (
                                        <div
                                            key={img.md5}
                                            class='position-relative border rounded overflow-hidden'
                                            style='width: 80px; height: 80px;'
                                        >
                                            <LazyImage
                                                src={img.s3SmallLink || img.s3MediumLink}
                                                lowResBase64={img.lowResBase64}
                                                className='w-100 h-100'
                                                style='height: 100%;'
                                                alt=''
                                            />
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-danger position-absolute p-0 d-flex align-items-center justify-content-center'
                                                onClick={this.removeAttachedImage}
                                                data-md5={img.md5}
                                                style='top: 2px; right: 2px; width: 20px; height: 20px; border-radius: 50%; font-size: 0.7em;'
                                            >
                                                <i class='fas fa-times' />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </> : <></>}
                    </> : <></>}
                </> : <>
                </>}
            </div>
        </>);
    }
}

export default Comments;
